<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { format, fromUnixTime } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import AgentPauseSessionsAPI from 'dashboard/api/agentPauseSessions';
import ReportHeader from './components/ReportHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();

const agents = computed(() => store.getters['agents/getAgents'] || []);
const selectedAgentId = ref(null);

const today = new Date();
const startOfToday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate()
);
const endOfToday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate(),
  23,
  59,
  59
);
const sinceRef = ref(Math.floor(startOfToday.getTime() / 1000));
const untilRef = ref(Math.floor(endOfToday.getTime() / 1000));

const sessions = ref([]);
const summary = ref({});
const isLoading = ref(false);
let activeRequestId = 0;

async function fetchReport() {
  activeRequestId += 1;
  const requestId = activeRequestId;
  isLoading.value = true;
  try {
    const params = {
      since: sinceRef.value,
      until: untilRef.value,
    };
    if (selectedAgentId.value) {
      params.user_id = selectedAgentId.value;
    }
    const accountId = store.getters.getCurrentAccountId;
    const response = await AgentPauseSessionsAPI.getPauseReport(
      accountId,
      params
    );
    if (requestId !== activeRequestId) return;
    sessions.value = response.data.sessions || [];
    summary.value = response.data.summary || {};
  } catch (error) {
    if (requestId !== activeRequestId) return;
    sessions.value = [];
    summary.value = {};
    useAlert(t('AGENT_PAUSE_TYPES.API.ERROR'));
  } finally {
    if (requestId === activeRequestId) isLoading.value = false;
  }
}

onMounted(async () => {
  await store.dispatch('agents/get');
  await fetchReport();
});

const summaryEntries = computed(() => Object.entries(summary.value));

function formatTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy HH:mm');
}

function onDateChange(event) {
  const { name, value } = event.target;
  if (!value) return;
  if (name === 'since') {
    sinceRef.value = Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000);
  } else if (name === 'until') {
    untilRef.value = Math.floor(new Date(`${value}T23:59:59`).getTime() / 1000);
  }
}

watch([sinceRef, untilRef, selectedAgentId], () => {
  fetchReport();
});
</script>

<template>
  <ReportHeader
    :header-title="t('AGENT_PAUSE_TYPES.REPORT.TITLE')"
    :header-description="t('AGENT_PAUSE_TYPES.REPORT.DESCRIPTION')"
  />

  <div class="flex flex-col gap-6">
    <div class="flex flex-wrap gap-4 items-end">
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11 uppercase">
          {{ t('AGENT_PAUSE_TYPES.REPORT.FILTERS.DATE_RANGE') }}
        </label>
        <div class="flex gap-2 items-center">
          <input
            type="date"
            name="since"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            :value="format(fromUnixTime(sinceRef), 'yyyy-MM-dd')"
            @change="onDateChange"
          />
          <span class="text-n-slate-11 text-sm">{{
            t('AGENT_PAUSE_TYPES.REPORT.TABLE.NO_DATA_SHORT')
          }}</span>
          <input
            type="date"
            name="until"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            :value="format(fromUnixTime(untilRef), 'yyyy-MM-dd')"
            @change="onDateChange"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11 uppercase">
          {{ t('AGENT_PAUSE_TYPES.REPORT.FILTERS.AGENT') }}
        </label>
        <select
          v-model="selectedAgentId"
          class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        >
          <option :value="null">
            {{ t('AGENT_PAUSE_TYPES.REPORT.FILTERS.AGENT') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <Button
        size="sm"
        :label="t('AGENT_PAUSE_TYPES.REPORT.FILTERS.APPLY')"
        :is-loading="isLoading"
        @click="fetchReport"
      />
    </div>

    <div
      v-if="summaryEntries.length"
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      <div
        v-for="[name, data] in summaryEntries"
        :key="name"
        class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4 flex flex-col gap-2"
      >
        <span class="text-sm font-semibold text-n-slate-12">{{ name }}</span>
        <div class="flex gap-4 text-sm text-n-slate-11">
          <span>{{
            t('AGENT_PAUSE_TYPES.REPORT.SUMMARY.COUNT_LABEL', {
              count: data.count,
            })
          }}</span>
          <span>{{
            t('AGENT_PAUSE_TYPES.REPORT.SUMMARY.TOTAL_MINUTES_LABEL', {
              minutes: data.total_minutes,
            })
          }}</span>
          <span
            v-if="
              data.completed_count != null &&
              data.count !== data.completed_count
            "
            class="text-n-amber-11"
            >{{
              t('AGENT_PAUSE_TYPES.REPORT.SUMMARY.ACTIVE_LABEL', {
                n: data.count - data.completed_count,
              })
            }}</span
          >
        </div>
      </div>
    </div>

    <div class="overflow-hidden rounded-lg border border-n-weak">
      <table class="w-full">
        <thead class="bg-n-alpha-black2">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.AGENT') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.PAUSE_TYPE') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.START_TIME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.END_TIME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.DURATION') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-if="isLoading">
            <td
              colspan="5"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.LOADING') }}
            </td>
          </tr>
          <tr v-else-if="!sessions.length">
            <td
              colspan="5"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ t('AGENT_PAUSE_TYPES.REPORT.TABLE.NO_DATA') }}
            </td>
          </tr>
          <template v-else>
            <tr v-for="session in sessions" :key="session.id">
              <td class="px-4 py-3 text-sm text-n-slate-12">
                {{ session.user_name }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ session.pause_type_name }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{
                  formatTime(session.started_at) ||
                  t('AGENT_PAUSE_TYPES.REPORT.TABLE.NO_DATA_SHORT')
                }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{
                  formatTime(session.ended_at) ||
                  t('AGENT_PAUSE_TYPES.REPORT.TABLE.ACTIVE')
                }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{
                  session.duration_minutes != null
                    ? session.duration_minutes
                    : t('AGENT_PAUSE_TYPES.REPORT.TABLE.ACTIVE')
                }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
